import React from "react";
import {
    DetailsListLayoutMode,
    SelectionMode,
    IColumn,
    ShimmeredDetailsList,
    IDetailsRowProps,
    DetailsRow,
    IDetailsListProps,
    CheckboxVisibility,
    nuOrderTheme as theme,
} from "@nuorder/components";
import { ILowerBoundaryRoleMap } from "containers/folders/types/base";
import { formatMessage } from "lib/globalize-util";
import { PermissionNameRenderer } from "containers/folders/containers/space-manager/components/manage-access/manage-access-permission-list/permission-name-renderer";
import { PermissionOptionsRenderer } from "containers/folders/containers/space-manager/components/manage-access/manage-access-permission-list/permission-options-renderer";
import { filterAndSortData } from "containers/folders/utils/permissions";
import {
    ASSORTMENT_DROPDOWN_WIDTH,
    DROPDOWN_WIDTH,
    PERMISSION_NAME_COLUMN_MIN_WIDTH,
    ASSORTMENT_PERMISSION_NAME_COLUMN_MAX_WIDTH,
} from "containers/folders/utils/constants";
import { DATATEST } from "constants/integration";

export interface IManageAccessPermissionListProps {
    personaData: nuorder.folder.v1.Permission[];
    onUpdateItempermission: (params) => void;
    onSetSelectedPermission: (params) => void;
    onOpenDeletePermissionDialog: () => void;
    merchantId: string;
    selectedItemId: string;
    LowerBoundaryRoleMap: ILowerBoundaryRoleMap;
    onFetchNextResults?: () => void;
    canFetchMore: boolean;
    shouldExcludeOrganizerPermission?: boolean;
    isChangePermissionLoading: boolean;
    selectedPermission: nuorder.folder.v1.Permission;
}

export const ManageAccessPermissionList: React.FC<IManageAccessPermissionListProps> = ({
    personaData,
    onUpdateItempermission,
    onOpenDeletePermissionDialog,
    onSetSelectedPermission,
    merchantId,
    selectedItemId,
    LowerBoundaryRoleMap,
    onFetchNextResults,
    canFetchMore,
    shouldExcludeOrganizerPermission,
    isChangePermissionLoading,
    selectedPermission,
}) => {
    const nameCellRenderer = React.useCallback((item: nuorder.folder.v1.Permission) => {
        return <PermissionNameRenderer item={item} />;
    }, []);

    const optionsCellRenderer = React.useCallback(
        (item: nuorder.folder.v1.Permission) => {
            return (
                <PermissionOptionsRenderer
                    item={item}
                    merchantId={merchantId}
                    LowerBoundaryRoleMap={LowerBoundaryRoleMap}
                    roleDropdownLowerBoundary={LowerBoundaryRoleMap[item.granted_to.member_id]}
                    onOpenDeletePermissionDialog={onOpenDeletePermissionDialog}
                    onSetSelectedPermission={onSetSelectedPermission}
                    onUpdatePermission={onUpdateItempermission}
                    selectedItemId={selectedItemId}
                    shouldExcludeOrganizerPermission={shouldExcludeOrganizerPermission}
                    isChangePermissionLoading={isChangePermissionLoading}
                    selectedPermission={selectedPermission}
                />
            );
        },
        [
            merchantId,
            LowerBoundaryRoleMap,
            onOpenDeletePermissionDialog,
            onSetSelectedPermission,
            onUpdateItempermission,
            selectedItemId,
            shouldExcludeOrganizerPermission,
            isChangePermissionLoading,
            selectedPermission,
        ]
    );
    const columnDefinitions = React.useMemo<IColumn[]>(() => {
        return [
            {
                key: "name",
                name: formatMessage("Who has access"),
                fieldName: "name",
                minWidth: PERMISSION_NAME_COLUMN_MIN_WIDTH,
                maxWidth: ASSORTMENT_PERMISSION_NAME_COLUMN_MAX_WIDTH,
                isResizable: true,
                isCollapsible: true,
                onRender: nameCellRenderer,
            },
            {
                key: "options",
                name: formatMessage("Role"),
                fieldName: "options",
                maxWidth: ASSORTMENT_DROPDOWN_WIDTH,
                onRender: optionsCellRenderer,
                minWidth: DROPDOWN_WIDTH,
            },
        ];
    }, [optionsCellRenderer, nameCellRenderer]);

    const getKey = React.useCallback((item: nuorder.folder.v1.Permission) => {
        return item?.id;
    }, []);
    const generateKey = React.useCallback((data: nuorder.folder.v1.Permission) => {
        return `${data?.id}_${data?.role}`;
    }, []);
    const handleRenderRow: IDetailsListProps["onRenderRow"] = React.useCallback((props: IDetailsRowProps) => {
        if (props) {
            const key = generateKey(props.item);
            return (
                <div
                    data-test={DATATEST.ASSORTMENTS.SHARE_RECIPIENT_ROW}
                    data-name={props.item.granted_to.display_name}
                    data-mail={props.item?.granted_to?.email}
                    data-id={props.item.id}
                    key={key}
                >
                    <DetailsRow
                        {...props}
                        styles={{
                            root: {
                                backgroundColor: `${theme.palette.white} !important`,
                                borderBottom: `1px solid ${theme.palette.neutralLight} !important`,
                                maxHeight: "MANAGE_ACCESS_LIST_ROW_MAX_HEIGHTpx",
                                paddingTop: "4px",
                            },
                        }}
                    />
                </div>
            );
        }
        return null;
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleRenderCustomPlaceholder = React.useCallback(
        (rowProps: IDetailsRowProps, index: number, defaultRender: (props: IDetailsRowProps) => React.ReactNode) => {
            onFetchNextResults?.();
            return defaultRender(rowProps);
        },
        [onFetchNextResults]
    );
    const items = React.useMemo(() => filterAndSortData(personaData), [personaData]);
    // NOTE: the [null] is to trigger handleRenderCustomPlaceholder for ShimmeredDetailsList.
    const itemsWithFlag = React.useMemo(() => items.concat(...(canFetchMore ? [null] : [])), [items, canFetchMore]);
    return (
        <ShimmeredDetailsList
            compact={true}
            onRenderCustomPlaceholder={handleRenderCustomPlaceholder}
            items={itemsWithFlag}
            columns={columnDefinitions}
            setKey="permissions"
            getKey={getKey}
            layoutMode={DetailsListLayoutMode.justified}
            selectionMode={SelectionMode.single}
            selectionPreservedOnEmptyClick={false}
            enterModalSelectionOnTouch={false}
            onRenderRow={handleRenderRow}
            checkboxVisibility={CheckboxVisibility.hidden}
        />
    );
};
